import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const MessageTrendChart = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMessageTrend();
  }, [interval]);

  const fetchMessageTrend = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_trends/message_trend/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch message trend data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching message trend:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatMessageDataForChart = (data, intervalLabel) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const firstItem = data[0];

    // YEAR DATA (e.g., [2024, 123])
    if (typeof firstItem[0] === "number" && firstItem[0] > 1000) {
      return data.map((item) => ({
        name: item[0].toString(),
        messages: item[1],
      }));
    }

    // MONTH DATA (e.g., [1, "January", 123])
    if (firstItem.length === 3) {
      return data.map((item) => ({
        name: item[1].trim(),
        messages: item[2],
      }));
    }

    // DATE / TIMESTAMP DATA
    if (typeof firstItem[0] === "string") {
      return data.map((item) => {
        const dateStr = item[0];
        const date = new Date(dateStr);

        let displayName = dateStr;

        if (!isNaN(date.getTime())) {
          if (intervalLabel.toLowerCase().includes("minute")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } else if (intervalLabel.toLowerCase().includes("hour")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              hour12: true,
            });
          } else {
            displayName = date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          }
        }

        return {
          name: displayName,
          messages: item[1],
        };
      });
    }

    // Fallback
    return data.map((item, index) => ({
      name: `Point ${index + 1}`,
      messages: Array.isArray(item) ? item[1] : item,
    }));
  };

  const chartData = useMemo(
    () => formatMessageDataForChart(data, interval),
    [data, interval]
  );

  if (loading) {
    return <div style={{ textAlign: "center" }}>Loading...</div>;
  }

  if (error) {
    return <div style={{ textAlign: "center", color: "red" }}>Error: {error}</div>;
  }

  if (!chartData.length) {
    return <div style={{ textAlign: "center" }}>📊 No data available</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={chartData.length > 10 ? -45 : 0}
          textAnchor={chartData.length > 10 ? "end" : "middle"}
          height={chartData.length > 10 ? 60 : 30}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
        />
        <Tooltip
          formatter={(value) => [value.toLocaleString(), "Messages"]}
        />
        <Legend formatter={() => "Message Trend"} />
        <Line
          type="monotone"
          dataKey="messages"
          stroke="#667eea"
          strokeWidth={2}
          dot={{ fill: "#667eea", r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MessageTrendChart;
